import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, UserCog, Loader2, ShieldCheck, KeyRound, Users, ScanFace, Copy, Car, Search, Hash, X, RotateCcw, Pencil, LockKeyhole, ShieldOff } from "lucide-react";
import { OfficerShiftCard, OfficerAccessDialog } from "@/components/OfficerAccess";
import { EmployeeEditDialog } from "@/components/EmployeeEditDialog";
import { IconAction } from "@/components/IconAction";
import { useWindowed, ShowMoreBar } from "@/components/ShowMore";
import { useDebounced } from "@/lib/useDebounced";
import { LoadMoreBar, PAGE_SIZE as SERVER_PAGE, totalFrom } from "@/components/LoadMoreBar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { displayName } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import EmployeeProfileDialog from "@/components/profile/EmployeeProfileDialog";

const ROLE_OPTIONS = [
  { v: "officer", label: "Officer", desc: "Runs the kiosk, signs visitors in and out." },
  { v: "staff", label: "Staff", desc: "Clocks in/out on the kiosk via PIN. No login." },
  { v: "teacher", label: "Teacher", desc: "Logs in to message their class's parents and view their roster." },
  { v: "driver", label: "Bus driver", desc: "Logs in to board students on/off the bus, announce arrival and share GPS." },
  { v: "admin", label: "Admin", desc: "Full management of this location." },
];

const ROLE_TONE = {
  admin: "bg-blue-100 text-blue-800",
  officer: "bg-slate-100 text-slate-700",
  staff: "bg-amber-100 text-amber-800",
  teacher: "bg-emerald-100 text-emerald-800",
};

export default function OfficersPage() {
  const { user } = useAuth();
  const canCreateAdmin = user?.role === "admin" || user?.role === "corporate_admin";
  const canRemoveAdmin = user?.role === "corporate_admin";
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "officer", pin: "" });
  const [creating, setCreating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pinTarget, setPinTarget] = useState(null); // {id, name, role}
  const [pinValue, setPinValue] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const [companyType, setCompanyType] = useState("business");
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const dq = useDebounced(q.trim(), 300);
  const [total, setTotal] = useState(0);
  const [moreLoading, setMoreLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("");
  const [profileId, setProfileId] = useState(null);

  const roleOptions = ROLE_OPTIONS.filter((r) => !["teacher", "driver"].includes(r.v) || ["school", "daycare"].includes(companyType));

  const ql = q.trim().toLowerCase();
  const visiblePeople = people.filter((o) => !roleFilter || o.role === roleFilter); // text search is server-side
  const peopleWin = useWindowed(visiblePeople);

  const toggleCaller = async (o) => {
    try {
      await api.put(`/officers/${o.id}/dismissal`, { enabled: !o.can_call_pickup });
      setPeople((ps) => ps.map((p) => (p.id === o.id ? { ...p, can_call_pickup: !o.can_call_pickup } : p)));
      toast.success(`${o.name} ${o.can_call_pickup ? "removed as" : "is now a"} dismissal caller`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, c] = await Promise.all([api.get("/officers", { params: { q: dq || undefined, limit: SERVER_PAGE } }), api.get("/me/company")]);
      setPeople(pRes.data); setTotal(totalFrom(pRes));
      setCompanyType(c.data.type || "business");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dq]);

  const isStaff = form.role === "staff";

  const create = async (e) => {
    e.preventDefault();
    if (creating) return;
    if (isStaff && (!form.pin || !/^\d{4,10}$/.test(form.pin))) {
      toast.error("Staff need a 4–10 digit PIN.");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: form.name,
        role: form.role,
        origin_url: window.location.origin,
        ...(form.email ? { email: form.email } : {}),
        ...(form.pin ? { pin: form.pin } : {}),
      };
      const { data } = await api.post("/officers", payload);
      if (data.invite?.emailed) toast.success(`${form.name} added — "create your password" email sent to ${form.email}`);
      else toast.success(`${form.name} added as ${form.role}`);
      if (data.invite && !data.invite.emailed) setResetLink({ name: form.name, email: form.email, invite: true, ...data.invite });
      setForm({ name: "", email: "", password: "", role: form.role, pin: "" });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (o) => {
    if (!confirm(`Remove ${o.name}? They will no longer be able to sign in or clock in.`)) return;
    setDeletingId(o.id);
    try {
      await api.delete(`/officers/${o.id}`);
      toast.success(`${o.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const openPin = (o) => { setPinTarget(o); setPinValue(""); };
  const closePin = () => { setPinTarget(null); setPinValue(""); };

  const savePin = async (e) => {
    e?.preventDefault();
    if (!/^\d{4,10}$/.test(pinValue)) {
      toast.error("PIN must be 4–10 digits."); return;
    }
    setPinBusy(true);
    try {
      await api.put(`/officers/${pinTarget.id}/pin`, { pin: pinValue });
      toast.success(`PIN set for ${pinTarget.name}`);
      closePin();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setPinBusy(false);
    }
  };

  const clearPin = async (o) => {
    if (!confirm(`Clear ${o.name}'s PIN? They won't be able to clock in until a new PIN is set.`)) return;
    try {
      await api.delete(`/officers/${o.id}/pin`);
      toast.success(`PIN cleared for ${o.name}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const [resetLink, setResetLink] = useState(null); // fallback only — shown when the email could not be sent
  const [resetBusyId, setResetBusyId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [accessTarget, setAccessTarget] = useState(null);

  const sendResetLink = async (o) => {
    setResetBusyId(o.id);
    try {
      const { data } = await api.post(`/officers/${o.id}/reset-link`, { origin_url: window.location.origin });
      if (data.emailed) toast.success(`Password reset link emailed to ${o.email}`);
      else setResetLink({ name: o.name, email: o.email, ...data });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setResetBusyId(null);
    }
  };


  const resetMfa = async (o) => {
    if (!confirm(`Reset ${o.name}'s authenticator app? They'll receive sign-in codes by email until they set it up again.`)) return;
    try {
      await api.post(`/officers/${o.id}/mfa/reset`);
      toast.success(`Authenticator reset for ${o.name}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };
  const resetFace = async (o) => {
    if (!confirm(`Reset ${o.name}'s face enrollment? They'll clock in with PIN only until they enroll again.`)) return;
    try {
      await api.delete(`/officers/${o.id}/face`);
      toast.success(`Face enrollment reset for ${o.name}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="officers-page">
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">Team</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Employees</h1>
          <p className="text-sm text-slate-600 mt-1">
            Officers run the kiosk · Admins manage this location · Staff clock in/out by PIN. Set a PIN for anyone who needs to log payroll hours.
          </p>
        </div>
        <button
          onClick={() => { setForm({ name: "", email: "", password: "", role: "officer", pin: "" }); setAddOpen(true); }}
          data-testid="employee-add-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:shadow transition shrink-0"
        >
          <Plus className="h-4 w-4" /> Add employee
        </button>
      </header>
      <OfficerShiftCard />

      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) setAddOpen(false); }}>
        <DialogContent className="sm:max-w-lg" data-testid="employee-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" /> Add employee
            </DialogTitle>
            <DialogDescription>
              {isStaff
                ? "Staff clock in/out on the kiosk with a PIN — no login needed."
                : form.role === "admin"
                ? "Admins get full management access to this location."
                : form.role === "teacher"
                ? "Teachers sign in to message their class's parents and see their roster."
                : "Officers sign in to run the kiosk and check visitors in and out."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4 pt-1">
            <Field label="Role">
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="employee-role-select"
                className={inputCls}
              >
                {roleOptions.map((r) => (
                  <option key={r.v} value={r.v} disabled={r.v === "admin" && !canCreateAdmin}>
                    {r.label}
                  </option>
                ))}
              </select>
              <span className="block mt-1.5 text-xs text-slate-500">
                {ROLE_OPTIONS.find((r) => r.v === form.role)?.desc}
              </span>
            </Field>
            <Field label="Full name">
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="officer-name-input"
                className={inputCls}
                placeholder={isStaff ? "Maria Lopez" : "Officer Garcia"}
              />
            </Field>
            <div className="grid grid-cols-1 gap-3">
              <Field label={isStaff ? "Email (optional)" : "Email"}>
                <input
                  type="email"
                  required={!isStaff}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="officer-email-input"
                  className={inputCls}
                  placeholder="maria@example.com"
                />
              </Field>
            </div>
            {!isStaff && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 inline-flex items-start gap-2" data-testid="officer-invite-note">
                <KeyRound className="h-4 w-4 shrink-0 mt-0.5" /> They'll get an email with a secure link to create their own password (valid 7 days).
              </div>
            )}
            <Field label={isStaff ? "PIN (required for staff)" : "PIN (optional, for kiosk clock-in)"}>
              <input
                inputMode="numeric"
                pattern="\d*"
                maxLength={10}
                value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
                data-testid="officer-pin-input"
                className={inputCls}
                placeholder="4 digits"
              />
              {isStaff && form.email && (
                <span className="block mt-1.5 text-xs text-slate-500">
                  With an email + PIN, they'll automatically receive a face-check enrollment link.
                </span>
              )}
            </Field>
            <DialogFooter className="pt-2 gap-2 sm:gap-2">
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                data-testid="employee-add-cancel"
                className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                data-testid="officer-create-btn"
                className="h-10 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 text-sm font-medium text-white hover:shadow disabled:opacity-60 transition"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Add employee
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display text-lg font-semibold text-slate-900">All employees</div>
            <div className="text-xs text-slate-500">Sign-in users + PIN-only staff in this location</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="officers-search-input"
                placeholder="Search name or email…"
                className="h-9 w-52 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              data-testid="officers-role-filter"
              className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm focus:border-primary outline-none"
            >
              <option value="">All roles</option>
              {ROLE_OPTIONS.map((r) => <option key={r.v} value={r.v}>{r.v}</option>)}
            </select>
            <div className="text-xs uppercase tracking-widest text-slate-500 whitespace-nowrap">
              {ql || roleFilter ? `${visiblePeople.length} / ${people.length}` : `${people.length} total`}
            </div>
          </div>
        </div>
        <table className="w-full text-sm" data-testid="officers-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Role</th>
              <th className="px-5 py-3">Kiosk PIN</th>
              <th className="px-5 py-3">Face check</th>
              {companyType === "school" && <th className="px-5 py-3">Dismissal caller</th>}
              <th className="px-5 py-3">Added</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={companyType === "school" ? 8 : 7} className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && people.length === 0 && (
              <tr><td colSpan={companyType === "school" ? 8 : 7} className="px-5 py-10 text-center text-slate-500">No employees yet. Click "Add employee" to get started.</td></tr>
            )}
            {!loading && people.length > 0 && visiblePeople.length === 0 && (
              <tr><td colSpan={companyType === "school" ? 8 : 7} className="px-5 py-10 text-center text-slate-500">No employees match your search or filter.</td></tr>
            )}
            {peopleWin.items.map((o) => (
              <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`officer-row-${o.id}`}>
                <td className="px-5 py-3">
                  <button onClick={() => setProfileId(o.id)} data-testid={`officer-name-${o.id}`}
                    className="flex items-center gap-2 font-medium text-slate-900 hover:text-primary hover:underline text-left transition">
                    {o.role === "admin" ? <ShieldCheck className="h-4 w-4 text-primary" />
                      : o.role === "staff" ? <Users className="h-4 w-4 text-amber-600" />
                      : <UserCog className="h-4 w-4 text-slate-500" />}
                    {o.name && o.role === "officer" ? displayName(o.name) : o.name}
                  </button>
                </td>
                <td className="px-5 py-3 text-slate-700">{o.email || <span className="text-slate-400">—</span>}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${ROLE_TONE[o.role] || "bg-slate-100 text-slate-700"}`}>{o.role}</span>
                </td>
                <td className="px-5 py-3">
                  {o.has_pin ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                      <KeyRound className="h-3.5 w-3.5" /> Set
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  {o.face_enrolled ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold" data-testid={`officer-face-status-${o.id}`}>
                      <ScanFace className="h-3.5 w-3.5" /> Enrolled
                    </span>
                  ) : o.has_pin ? (
                    <span className="text-xs text-slate-400" data-testid={`officer-face-status-${o.id}`} title="They'll be asked to take a photo at the kiosk on their next clock-in">Enrolls at kiosk</span>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                {companyType === "school" && (
                  <td className="px-5 py-3">
                    <button
                      onClick={() => toggleCaller(o)}
                      data-testid={`officer-caller-toggle-${o.id}`}
                      title="Allowed to call car numbers at dismissal"
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                        o.can_call_pickup
                          ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                          : "border border-slate-200 bg-white text-slate-400 hover:text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <Car className="h-3 w-3" /> {o.can_call_pickup ? "Caller" : "Off"}
                    </button>
                  </td>
                )}
                <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <IconAction onClick={() => setEditTarget(o)} Icon={Pencil} label="Edit details" testid={`officer-edit-${o.id}`} />
                    {o.role !== "admin" && <IconAction onClick={() => setAccessTarget(o)} Icon={LockKeyhole} label="Console access" testid={`officer-access-${o.id}`} />}
                    <IconAction onClick={() => openPin(o)} Icon={Hash} label={o.has_pin ? "Reset PIN" : "Set PIN"} testid={`officer-pin-set-${o.id}`} />
                    {o.has_pin && (
                      <IconAction onClick={() => clearPin(o)} Icon={X} label="Clear PIN" testid={`officer-pin-clear-${o.id}`} />
                    )}
                    {o.mfa_enabled && (
                      <IconAction onClick={() => resetMfa(o)} Icon={ShieldOff} label="Reset authenticator app" testid={`officer-mfa-reset-${o.id}`} />
                    )}
                    {o.face_enrolled && (
                      <IconAction onClick={() => resetFace(o)} Icon={RotateCcw} label="Reset face" testid={`officer-face-reset-${o.id}`} />
                    )}
                    {o.email && (
                      <IconAction onClick={() => sendResetLink(o)} busy={resetBusyId === o.id} Icon={KeyRound}
                        label="Reset password" testid={`officer-reset-password-${o.id}`} />
                    )}
                    {(o.role === "officer" || o.role === "staff" || canRemoveAdmin) && o.id !== user?.id ? (
                      <IconAction onClick={() => remove(o)} busy={deletingId === o.id} Icon={Trash2} label="Remove" tone="danger" testid={`officer-delete-${o.id}`} />
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            <ShowMoreBar win={peopleWin} colSpan={8} testid="officers-show-more" />
            {!peopleWin.hasMore && total > people.length && (
              <tr><td colSpan={8}><LoadMoreBar shown={people.length} total={total} loading={moreLoading} testid="officers-load-more" onMore={async () => {
                setMoreLoading(true);
                try { const res = await api.get("/officers", { params: { q: dq || undefined, limit: SERVER_PAGE, offset: people.length } }); setPeople((p) => [...p, ...res.data]); setTotal(totalFrom(res)); }
                finally { setMoreLoading(false); }
              }} /></td></tr>
            )}
          </tbody>
        </table>
      </section>

      {profileId && (
        <EmployeeProfileDialog
          userId={profileId}
          companyType={companyType}
          onClose={() => setProfileId(null)}
          onOpenStudent={(sid) => navigate(`/admin/students/${sid}`)}
          actions={{
            setPin: (u) => { setProfileId(null); openPin(u); },
            resetPassword: (u) => { setProfileId(null); sendResetLink(u); },
          }}
        />
      )}


      <OfficerAccessDialog officer={accessTarget} isCare={companyType === "care"} onClose={() => setAccessTarget(null)} onSaved={() => load()} />
      {editTarget && (
        <EmployeeEditDialog person={editTarget} roleOptions={roleOptions} isSelf={editTarget.id === user?.id} canSetAdmin={canCreateAdmin}
          onClose={() => setEditTarget(null)}
          onSaved={(u) => { setPeople((ps) => ps.map((p) => (p.id === u.id ? { ...p, ...u } : p))); setEditTarget(null); }} />
      )}

      <Dialog open={!!resetLink} onOpenChange={(o) => !o && setResetLink(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="reset-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" /> {resetLink?.invite ? "Create-password link" : "Password reset link"} · {resetLink?.name}
            </DialogTitle>
            <DialogDescription>
              {resetLink?.error
                ? `The email to ${resetLink?.email} could not be sent (${resetLink.error}) — share this link with them directly instead. It expires in ${resetLink?.invite ? "7 days" : "30 minutes"}.`
                : `Email isn't configured yet — share this link with them directly. It expires in ${resetLink?.invite ? "7 days" : "30 minutes"}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 pt-2">
            <input
              readOnly
              value={resetLink?.link || ""}
              data-testid="reset-link-input"
              className="flex-1 h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-xs text-slate-700 outline-none"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={() => { navigator.clipboard?.writeText(resetLink?.link || ""); toast.success("Link copied"); }}
              data-testid="reset-link-copy"
              className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-white inline-flex items-center gap-2 hover:shadow transition"
            >
              <Copy className="h-4 w-4" /> Copy
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinTarget} onOpenChange={(o) => !o && closePin()}>
        <DialogContent className="sm:max-w-md" data-testid="pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" /> {pinTarget?.has_pin ? "Reset PIN" : "Set PIN"} · {pinTarget?.name}
            </DialogTitle>
            <DialogDescription>
              4–10 digit number used to clock in/out at the kiosk. Make it memorable but not their birth year.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={savePin} className="space-y-3 pt-2">
            <input
              autoFocus
              inputMode="numeric"
              pattern="\d*"
              maxLength={10}
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ""))}
              data-testid="pin-dialog-input"
              className="w-full h-12 text-center font-display text-2xl tracking-[0.4em] rounded-lg border border-slate-200 bg-white px-3 focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              placeholder="••••"
            />
            <DialogFooter className="pt-2 gap-2 sm:gap-2">
              <button type="button" onClick={closePin} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
                Cancel
              </button>
              <button type="submit" disabled={pinBusy || pinValue.length < 4} data-testid="pin-dialog-save"
                      className="h-10 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white hover:shadow disabled:opacity-60 transition">
                {pinBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                Save PIN
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputCls =
  "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none disabled:bg-slate-50 disabled:text-slate-400";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">{label}</span>
      {children}
    </label>
  );
}

